'use client';

import { useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import {
  ASPECT_RATIOS,
  COMMON_ASPECT_RATIOS,
  type AspectRatioKey,
} from '@/shared/constants/aspect-ratios';
import { AspectRatioChip, FreeAspectRatioChip } from './AspectRatioChip';

interface AspectRatioSelectorProps {
  /** Currently selected aspect ratio key, or null for free crop */
  selectedRatio: AspectRatioKey | null;
  /** Callback when ratio is selected */
  onRatioSelect: (ratio: AspectRatioKey | null) => void;
  /** Show all ratios or just common ones */
  showAllRatios?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Horizontal scrollable selector for aspect ratios
 * Shows common ratios by default, with option to show all
 */
export function AspectRatioSelector({
  selectedRatio,
  onRatioSelect,
  showAllRatios = false,
  className,
}: AspectRatioSelectorProps) {
  const t = useTranslations('aspectRatio');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedChipRef = useRef<HTMLDivElement>(null);

  // Get ratios to display
  const ratioKeys = showAllRatios
    ? (Object.keys(ASPECT_RATIOS) as AspectRatioKey[])
    : COMMON_ASPECT_RATIOS;

  // Scroll selected chip into view when selection changes
  useEffect(() => {
    if (selectedChipRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const chip = selectedChipRef.current;

      const containerRect = container.getBoundingClientRect();
      const chipRect = chip.getBoundingClientRect();

      // Check if chip is outside visible area
      if (chipRect.left < containerRect.left) {
        // Scroll left
        container.scrollBy({
          left: chipRect.left - containerRect.left - 16,
          behavior: 'smooth',
        });
      } else if (chipRect.right > containerRect.right) {
        // Scroll right
        container.scrollBy({
          left: chipRect.right - containerRect.right + 16,
          behavior: 'smooth',
        });
      }
    }
  }, [selectedRatio]);

  return (
    <div className={cn('w-full', className)}>
      <div
        ref={scrollContainerRef}
        className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {/* Free aspect ratio option */}
        <div ref={selectedRatio === null ? selectedChipRef : undefined}>
          <FreeAspectRatioChip
            isSelected={selectedRatio === null}
            onClick={() => onRatioSelect(null)}
            label={t('free')}
          />
        </div>

        {/* Aspect ratio options */}
        {ratioKeys.map((key) => (
          <div
            key={key}
            ref={selectedRatio === key ? selectedChipRef : undefined}
          >
            <AspectRatioChip
              config={ASPECT_RATIOS[key]}
              isSelected={selectedRatio === key}
              onClick={() => onRatioSelect(key)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
